/**
 * ComingSoon — now the public "Business Update" page.
 * ─────────────────────────────────────────────────────────
 * August 2026: Alaine's Bench™ is no longer offered as a public
 * subscription product. The marketing storefront (trial banner, demo,
 * tour, register CTAs) was retired at the founder's direction. The
 * platform is now an internal, proprietary tool used by Alaine & Co.
 * to serve consulting clients. Public visitors see the announcement;
 * the Sign in link remains for the founder and provisioned clients.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ComingSoon() {
  const { user } = useAuth();

  // Founder / provisioned accounts go straight to the studio.
  if (user && user.has_access) return <Navigate to="/dashboard" replace />;

  return (
    <div className="platform-blue min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 shrink-0 rounded-md bg-emerald-800 text-white grid place-items-center font-bold">A</div>
            <div className="leading-none min-w-0">
              <div className="font-bold text-lg tracking-tight">Alaine's Bench<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">An Alaine &amp; Co. Platform</div>
            </div>
          </div>
          <Link to="/login" className="text-sm text-stone-500 hover:text-emerald-800" data-testid="founder-login-link">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 lg:px-8 pt-20 pb-24" data-testid="business-update">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-800 font-bold mb-6">
          A Business Update from Alaine &amp; Co.
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-stone-900">
          Alaine's Bench<sup className="text-[0.45em] align-super">™</sup> is now
          the proprietary platform behind Alaine&nbsp;&amp;&nbsp;Co.
        </h1>

        <div className="mt-10 space-y-6 text-stone-700 text-lg leading-relaxed">
          <p>
            Over the past year, I've invested a tremendous amount of time developing
            Alaine's Bench™, a proprietary platform built from more than two decades
            of experience in food science, formulation, commercialization, and
            product development.
          </p>
          <p>
            After careful consideration, I've made the decision not to offer public
            subscriptions.
          </p>
          <p>
            Instead, Alaine's Bench™ will remain an exclusive, proprietary platform
            used internally by Alaine &amp; Co. to support our consulting clients and
            technical projects.
          </p>
          <div>
            <p className="font-medium text-stone-900">This decision allows us to:</p>
            <ul className="mt-3 space-y-2 list-disc pl-6">
              <li>Deliver faster, more efficient technical solutions.</li>
              <li>Maintain a consistent, high-quality consulting process.</li>
              <li>Protect the intellectual property and methodologies that differentiate Alaine &amp; Co.</li>
              <li>Continue investing in new capabilities that directly benefit our clients.</li>
            </ul>
          </div>
          <p>
            Our clients won't just be hiring a consultant&mdash;they'll be working with
            a firm supported by proprietary technology designed specifically to
            accelerate formulation, commercialization, and technical decision-making.
          </p>
          <p className="font-medium text-stone-900">
            Sometimes the greatest value isn't in selling the tool&mdash;it's in using
            the tool to deliver exceptional results.
          </p>
          <p>I'm excited about what's ahead as Alaine &amp; Co. continues to grow.</p>
        </div>

        <div className="mt-10 border-t border-stone-200 pt-6">
          <div className="font-bold text-stone-900">&mdash; Courtney Stokes, MBA</div>
          <div className="text-stone-600 text-sm mt-1">Founder &amp; Food Scientist<br />Alaine &amp; Co.</div>
        </div>

        <div className="mt-12 rounded-lg bg-emerald-50 border border-emerald-200 p-6">
          <div className="font-semibold text-emerald-900">Working on a food product?</div>
          <p className="mt-1.5 text-sm text-stone-700 leading-relaxed">
            Alaine &amp; Co. offers hands-on formulation, labeling, and
            commercialization consulting &mdash; powered by this platform.
          </p>
          <a
            href="https://alaineandco.com/"
            className="mt-4 inline-block px-5 py-2.5 rounded-md bg-emerald-800 hover:bg-emerald-700 text-white font-medium text-sm transition-colors"
            data-testid="visit-alaine-co-link"
          >
            Visit AlaineAndCo.com
          </a>
        </div>
      </main>

      <footer className="border-t border-stone-200 py-8">
        <div className="max-w-2xl mx-auto px-6 text-xs text-stone-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>&copy; {new Date().getFullYear()} Alaine &amp; Co. All rights reserved.</span>
          <Link to="/terms" className="hover:text-emerald-800">Terms</Link>
          <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
